import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "./components/scrolltotop/ScrollToTop";

// Eagerly loaded: home route + always-mounted modal content + 404 fallback
import HomeThree from "./pages/homepages/HomeThree";
import ContactUs from "./pages/innerpages/ContactUs";
import Error from "./pages/innerpages/Error";

// Import Css Here
import "./assets/scss/style.scss";
import "sweetalert2/dist/sweetalert2.min.css";
import "boxicons/src/index";
import "boxicons/css/boxicons.min.css";
import "boxicons/css/animations.css";
import "boxicons/css/transformations.css";

import Swal from "sweetalert2/dist/sweetalert2.all.min";
import { Modal } from "react-bootstrap";
import Cookies from 'js-cookie';
import { trackPageView } from "./utils/analytics";

// Lazily loaded route pages — code-splitting keeps the initial (home) bundle small
const Faq = React.lazy(() => import("./pages/innerpages/Faq"));
const PhoneService = React.lazy(() => import("./pages/innerpages/PhoneService"));
const ComingSoon = React.lazy(() => import("./pages/innerpages/ComingSoon"));
const HappyGastro = React.lazy(() => import("./pages/detailspages/HappyGastro"));
const HappyPOS = React.lazy(() => import("./pages/detailspages/HappyPOS"));
const RegisterService = React.lazy(() => import("./pages/innerpages/RegisterService"));
const Demo = React.lazy(() => import("./innerpages/Demo"));
const HappyHardwares = React.lazy(() => import("./innerpages/HappyHardwares"));
const Career = React.lazy(() => import("./innerpages/Career"));
const ModulePrices = React.lazy(() => import("./innerpages/ModulePrices"));
const Integrations = React.lazy(() => import("./innerpages/Integrations"));
const PricingPlan = React.lazy(() => import("./innerpages/PricingPlan"));
const AszfIndex = React.lazy(() => import("./pages/innerpages/AszfIndex"));
const AdatkezelesIndex = React.lazy(() => import("./pages/innerpages/AdatkezelesIndex"));
const DeliveryModule = React.lazy(() => import("./components/about/AboutFour"));
const Devices = React.lazy(() => import("./innerpages/components/Devices"));
const HappySoftwares = React.lazy(() => import("./innerpages/HappySoftwares"));
const HappyServices = React.lazy(() => import("./innerpages/HappyServices"));
const Profile = React.lazy(() => import("./innerpages/Profile"));
const DeviceDetails = React.lazy(() => import("./pages/detailspages/DeviceDetails"));
const PartnerProgram = React.lazy(() => import("./innerpages/PartnerProgram"));
const InvoiceIndex = React.lazy(() => import("./pages/invoice"));
const ServicePrices = React.lazy(() => import("./innerpages/ServicePrices"));
const Tos = React.lazy(() => import("./components/mobile/Tos"));
const ASZF = React.lazy(() => import("./components/mobile/ASZF"));
const OrderTracking = React.lazy(() => import("./pages/order/OrderTracking"));
const AboutUs = React.lazy(() => import("./pages/homepages/aboutUs"));
const Palyazat = React.lazy(() => import("./innerpages/Palyazat"));
const BookingPage = React.lazy(() => import("./innerpages/BookingPage"));
const HomeFour = React.lazy(() => import("./pages/homepages/HomeFour"));
const HomeFive = React.lazy(() => import("./pages/homepages/HomeFive"));
const EventGrid = React.lazy(() => import("./pages/innerpages/EventGrid"));

// The niche service pages share one module; load it once as a dedicated chunk
const loadNiche = () => import("./innerpages/components/services/NicheAppPage");
const LogisticAppPage = React.lazy(() => loadNiche().then((m) => ({ default: m.LogisticAppPage })));
const ServiceAppPage = React.lazy(() => loadNiche().then((m) => ({ default: m.ServiceAppPage })));
const GastroPage = React.lazy(() => loadNiche().then((m) => ({ default: m.GastroPage })));
const CrmPage = React.lazy(() => loadNiche().then((m) => ({ default: m.CrmPage })));
const ErpPage = React.lazy(() => loadNiche().then((m) => ({ default: m.ErpPage })));
const TradePage = React.lazy(() => loadNiche().then((m) => ({ default: m.TradePage })));
const ServicesBookingPage = React.lazy(() => loadNiche().then((m) => ({ default: m.ServicesBookingPage })));
const EnergyPage = React.lazy(() => loadNiche().then((m) => ({ default: m.EnergyPage })));
const ProjectPage = React.lazy(() => loadNiche().then((m) => ({ default: m.ProjectPage })));
const SalonPage = React.lazy(() => loadNiche().then((m) => ({ default: m.SalonPage })));

global.alert = (message) => {
    Swal.fire({
        titleText: message,
        confirmButtonColor: "#F2B34C",
        confirmButtonText: "Rendben",
        showCancelButton: false,
        showDenyButton: false,
    });
};
window.alert = (message) => {
    Swal.fire({
        titleText: message,
        confirmButtonColor: "#F2B34C",
        confirmButtonText: "Rendben",
        showCancelButton: false,
        showDenyButton: false,
    });
};

function DomainRedirector() {
    const location = useLocation();
    const navigate = useNavigate();

    // Fire a virtual page view on every SPA route change.
    useEffect(() => {
        trackPageView(location.pathname + (location.search || ''), document.title);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const host = (window.location.hostname || "").replace(/^www\./i, "").toLowerCase();
        const path = location.pathname || "/";

        // Root -> szolgáltatás landing, domain alapján
        if ((path === "/" || path === "") && host === "happysolutions.hu") {
            navigate("/", { replace: true });

            return;
        }
        if ((path === "/" || path === "") && host === "happygastro.hu") {
            navigate("/gastro-ettermi-szoftver", { replace: true });

            return;
        }
        if ((path === "/" || path === "") && host === "happytrade.hu") {
            navigate("/trade-kereskedelmi-rendszer", { replace: true });
        }
    }, [location.pathname, navigate]);

    return null;
}

function App() {
    const [showContactModal, setShowContactModal] = useState(false);

    useEffect(() => {
        // Hide animation
        window.hideSplashScreen()

        new Promise(() => setTimeout(() => {
            document.addEventListener("mouseleave", function () {
                if (!showContactModal && Cookies.get('disabledContactModal') === undefined) {
                    Cookies.set("marketing-tools-enabled", "true", { expires: 1 });
                    setShowContactModal(true);
                }
            });
        }, 10 * 1000));
    }, []);

    // Expose a global opener for the contact modal so CTAs can trigger it
    useEffect(() => {
        window.openContactModal = () => {
            try {
                Cookies.set('marketing-tools-enabled', 'true', { expires: 1 }); 
            } catch (e) {
                /* ignore */
            }
            try {
                const { trackEvent } = require('./utils/analytics');

                trackEvent('modal_open', {
                    modal: 'contact',
                    page_path: window.location.pathname 
                });
            } catch (e) { /* noop */ }
            setShowContactModal(true);
        };

        return () => {
            try {
                delete window.openContactModal; 
            } catch (e) {
                /* ignore */
            }
        };
    }, []);

    const langBasename = (() => {
        const m = window.location && window.location.pathname && window.location.pathname.match(/^\/(en|de)(\/|$)/i);

        return m ? `/${m[1].toLowerCase()}` : "/";
    })();

    return (
        <Router basename={langBasename}>
            <ScrollToTop>
                <DomainRedirector />
                <Modal
                    size={'xl'}
                    centered
                    show={showContactModal}
                    className={'contact-modal'}
                    backdrop
                    onHide={() => {
                        Cookies.set('contact-modal-closed', 'true', { expires: 1 });
                        Cookies.set('disabledContactModal', 'true', { expires: 1 });
                        try {
                            const { trackEvent } = require('./utils/analytics');

                            trackEvent('modal_close', { modal: 'contact' });
                        } catch (e) { /* noop */ }
                        setShowContactModal(false);
                    }}
                >
                    <Modal.Body>
                        <ContactUs modalMode={true}/>
                    </Modal.Body>
                </Modal>
                <React.Suspense fallback={null}>
                    <Routes>

                        <Route path={"/"} element={<HomeThree/>}/>

                        <Route path={"/kapcsolat/elerhetosegeink"} element={<ContactUs/>}/>
                        <Route path={"/kapcsolat/ugyfelszolgalat"} element={<ContactUs/>}/>
                        <Route path={"/kapcsolat/gyik"} element={<Faq/>}/>
                        <Route path={"/kapcsolat/dokumentumok"} element={<ContactUs/>}/>
                        <Route path={"/termekeink/happy-gastro"} element={<HappyGastro/>}/>

                        <Route path={"/termekeink/happy-pos"} element={<HappyPOS/>}/>
                        <Route path={"/termekeink/szoftver"} element={<HappySoftwares/>}/>
                        <Route path={"/termekeink/hardver"} element={<HappyHardwares/>}/>
                        <Route path={"/termekeink/hardver/:deviceSlug"} element={<DeviceDetails/>}/>
                        <Route path={"/termekek"} element={<HappyServices/>}/>

                        <Route path={"/erp-vallalatiranyitasi-rendszer"} element={<ErpPage/>}/>
                        <Route path={"/crm-rendszer"} element={<CrmPage/>}/>
                        <Route path={"/trade-kereskedelmi-rendszer"} element={<TradePage/>}/>
                        <Route path={"/gastro-ettermi-szoftver"} element={<GastroPage/>}/>
                        <Route path={"/services-idopontfoglalo-rendszer"} element={<ServicesBookingPage/>}/>
                        <Route path={"/service-idopontfoglalo-app"} element={<ServiceAppPage/>}/>
                        <Route path={"/logistic-app"} element={<LogisticAppPage/>}/>
                        <Route path={"/palyazat"} element={<Palyazat/>}/>

                        <Route path={"/foglalas"} element={<BookingPage/>}/>
                        <Route path={"/booking"} element={<BookingPage/>}/>

                        <Route path={"/termekek/energy"} element={<EnergyPage/>}/>
                        <Route path={"/termekek/project"} element={<ProjectPage/>}/>
                        <Route path={"/termekek/salon"} element={<SalonPage/>}/>
                        <Route path={"/termekeink/hazhozszallitas-modul"} element={<DeliveryModule/>}/>
                        <Route path={"/termekeink/ettermi-szoftver"} element={<HappyGastro/>}/>
                        <Route path={"/termekeink/mobil-pincer-app"} element={<HappyPOS/>}/>
                        <Route path={'/termekeink/eszkozok'} element={<Devices/>}/>
                        <Route path={"/demo"} element={<Demo/>}/>
                        <Route path={"/karrier"} element={<Career/>}/>
                        <Route path={'/profilom'} element={<Profile/>}/>
                        <Route path={"/partner-program"} element={<PartnerProgram/>}/>
                        <Route path={"/szolgaltatasok/it-rendszergazda"} element={<ComingSoon/>}/>
                        <Route path={"/szolgaltatasok/email-tarhely"} element={<ComingSoon/>}/>
                        <Route path={"/szolgaltatasok/telefonkozpont"} element={<PhoneService/>}/>
                        <Route path={"/szolgaltatasok/arajanlat"} element={<PricingPlan/>}/>
                        <Route path={"/szolgaltatasok/arajanlat/csomag-arak"} element={<PricingPlan/>}/>
                        <Route path={"/szolgaltatasok/arajanlat/modul-arak"} element={<ModulePrices/>}/>
                        <Route path={"/szolgaltatasok/arajanlat/szolgaltatas-arak"} element={<ServicePrices/>}/>
                        <Route path={"/szolgaltatasok/partner/regisztracio"} element={<RegisterService/>}/>
                        <Route path={"/mobile/tos"} element={<Tos/>}/>
                        <Route path={"/mobile/privacy-policy"} element={<ASZF/>}/>

                        <Route path={"/signup/plans"} element={<ComingSoon/>}/>
                        <Route path={"/kapcsolat/elerhetosegeink"} element={<ContactUs/>}/>
                        <Route path={"/elerhetosegeink"} element={<ContactUs/>}/>
                        <Route path={"/aszf"} element={<AszfIndex/>}/>
                        <Route path={"/adatkezelesi-tajekoztato"} element={<AdatkezelesIndex/>}/>
                        <Route path={'/eszkozok'} element={<Devices/>}/>
                        <Route path={'/integraciok'} element={<Integrations/>}/>
                        <Route path={'/invoice/:orderId'} element={<InvoiceIndex/>}/>
                        <Route path={'/szamla/:orderId'} element={<InvoiceIndex/>}/>
                        <Route path={'/order/tracking/:orderId'} element={<OrderTracking/>}/>
                        <Route path={'/about-us'} element={<AboutUs/>}/>

                        <Route exact path={`${process.env.PUBLIC_URL + "/home-3"}`} element={<HomeThree/>}/>
                        <Route exact path={`${process.env.PUBLIC_URL + "/home-4"}`} element={<HomeFour/>}/>
                        <Route exact path={`${process.env.PUBLIC_URL + "/home-5"}`} element={<HomeFive/>}/>
                        <Route exact path={`${process.env.PUBLIC_URL + "/event-grid"}`} element={<EventGrid/>}/>

                        <Route path="*" element={<Error/>}/>
                    </Routes>
                </React.Suspense>
            </ScrollToTop>
        </Router>
    );
}

export default App;
